import {
  assert_array_is_empty,
  assert_instanceof,
  assert_not_equal,
  assert_true,
  assert_type_boolean,
  assert_type_enum,
  not_implemented,
} from '@shared/asserts/utils_asserts';
import { assert_string_uuid } from '@shared/asserts/utils_asserts_string';
import { DTO__base } from '@shared/common/DTO__base';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { FIELD__base } from '@shared/dto/generation/graph_invoke/_fields/_base/FIELD__base';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { MAP__field_data, MAP__fields_by_id, TYPE_CLASS_CONSTRUCTOR_DTO } from '@shared/types/types';
import { UTILS__type } from '@shared/utils/UTILS__type';
import { DTO_GRAPH__instance } from '../../../_graph/DTO_GRAPH__instance';
import { ENUM__field_connectable_type } from '../../_base/_methods/ENUM__field_connectable_type';
import { CONSTRUCTOR_FIELD_CONTAINER } from '../../_base/CONSTRUCTOR_FIELD_CONTAINER';
import { DTO_META__field_info } from '../../_base/DTO_META__field_info';
import { PROPS__serialization } from '../../_base/FIELD__base.store';
import { FIELD__base_visible_connectable } from '../../_base/FIELD__base_connectable';
import { DECORATOR__field_is_input } from '../_base/DECORATOR__field_is_input';
import { DECORATOR__field_not_linked } from '../_base/DECORATOR__field_not_linked';

export class FIELD__list_base_generic<T extends FIELD__base> extends FIELD__base_visible_connectable {
  /*
   * STATIC FIELDS
   */

  static _class_name_static = ENUM__dto_class_name.ABSTRACT;

  /*
   * STATIC METHODS
   */

  static create_field_list_dto<T extends DTO__base>(class_object: TYPE_CLASS_CONSTRUCTOR_DTO<T>) {}

  /*
   * FIELDS
   */
  items!: T[];
  item_connectable_type!: ENUM__field_connectable_type;
  buttons_add_remove_are_disabled!: boolean;

  item_options: MAP__serializable<keyof T, any>;
  item_meta: MAP__serializable<keyof DTO_META__field_info, any>;

  /*
   * PROPERTIES
   */

  field_connectable__value(graph: DTO_GRAPH__instance) {
    return `${this.items.length} items`;
  }

  field_connectable__type() {
    return ENUM__field_connectable_type.LIST;
  }

  field_connectable__type_item() {
    return this.item_connectable_type;
  }

  fields__get_map(with_children: boolean): MAP__fields_by_id {
    let out = super.fields__get_map(with_children);

    // todo:remove
    // if (out.size() > 0) {
    //   debugger;
    //   // todo: check why size > 0
    //   out = super.fields__get_map(with_children);
    // }

    for (let field of this.items) {
      assert_string_uuid(field.meta.field_id);

      out.set(field.meta.field_id, field);

      if (with_children) {
        let fields_list_children = field.fields__get_map(true);
        out.merge_with(fields_list_children);
      }
    }

    return out;
  }

  sort_items(items: T[]): void {
    not_implemented();
  }

  /*
   * METHODS
   */

  constructor() {
    super();

    this.items = [];
    this.buttons_add_remove_are_disabled = true;

    this.item_options = new MAP__serializable();
    this.item_meta = new MAP__serializable();
  }

  create_item_new(add_to_items = true): T {
    let out = this.create_item_new__default();

    if (add_to_items) {
      this.items.push(out);
    }

    return out;
  }

  create_item_new__default(): T {
    let out = this._create_item_internal__default();

    if (!this.item_options.is_empty()) {
      out = out.clone(this.item_options.to_object() as Partial<T>);
    }

    this._init_item_new(out);

    return out;
  }

  protected _init_item_new(item: T) {
    let item_class = item.constructor as unknown as CONSTRUCTOR_FIELD_CONTAINER<FIELD__base>;

    let item_meta_options = { ...item_class.get_meta_params_default(), ...this.item_meta.to_object() };

    item.meta = new DTO_META__field_info().clone(item_meta_options);
    item.meta.field_hash__need_include_path = false;
    item.meta.id_create();
    item.post_init();

    item.copy_meta_from_parent(this);

    if (this.meta.data__is_linked) {
      let { meta } = item;

      [meta.data__is_linked, meta.data__synced_by_parent_id] = [true, this.meta.field_id];
    }

    let fields_children = item.fields__get_map(true).values();

    for (let field_child of fields_children) {
      field_child.copy_meta_from_parent(item);

      if (this.meta.data__is_linked) {
        let { meta } = field_child;

        [meta.data__is_linked, meta.data__synced_by_parent_id] = [true, this.meta.field_id];
      }
    }

    let field_name = `${this.meta.field_name}:${this.items.length}`;
    let field_path = `${this.meta.field_path}:${this.items.length}`;

    item.fill_field_name_and_path(field_name, field_path);
  }

  item_remove(item_to_remove: T) {
    let index_item = this.items.findIndex(item => item.meta.field_id == item_to_remove.meta.field_id);
    assert_not_equal(index_item, -1);
    this.items.splice(index_item, 1);

    this.field_name_and_path__fill_for_children();
  }

  items__remove_all() {
    this.items.length = 0;
  }

  field_name_and_path__fill_for_children() {
    for (let [index, item] of this.items.entries()) {
      let field_name = `${this.meta.field_name}:${index}`;
      let field_path = `${this.meta.field_path}:${index}`;

      item.fill_field_name_and_path(field_name, field_path);
    }
  }

  field_name_and_path__reset_for_children() {
    for (let item of this.items) {
      item.fill_field_name_and_path(this.meta.field_name, this.meta.field_path);
    }
  }

  protected _create_item_internal__default(): T {
    not_implemented();
  }

  /*
   *
   */

  hash__update_with_graph(graph: DTO_GRAPH__instance) {
    //hash children.
    // need hashed children before hash_create_with_graph__case_input to return empty hash if not items with non zero hash
    for (let item of this.items) item.hash__update_with_graph(graph);

    super.hash__update_with_graph(graph);
  }

  validate(graph: DTO_GRAPH__instance) {
    super.validate(graph);

    for (const item of this.items) item.validate(graph);
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  has_effect_on_generation_runtime(graph: DTO_GRAPH__instance) {
    let items_sources = this.items.map(item => item.find_source(graph));

    let is_any_item_has_effect = items_sources.some(item => item.has_effect_on_generation_runtime(graph));
    if (!is_any_item_has_effect) return false;

    return super.has_effect_on_generation_runtime(graph);
  }

  /*
   *
   */

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  store__serialization_keys_list_black(): string[] {
    let out: (keyof this)[] = super.store__serialization_keys_list_black() as (keyof this)[];

    out.push('item_options');
    out.push('item_meta');
    out.push('buttons_add_remove_are_disabled');
    out.push('item_connectable_type');

    return out as string[];
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  store__serialization_keys_list_white_for_hash(): string[] {
    let out: (keyof this)[] = [];

    out.push('items');

    return out as string[];
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  store__serialize_field(
    graph: DTO_GRAPH__instance,
    //
    props: PROPS__serialization,
    container: MAP__field_data,
    //
    field_name: string,
    field_value: any,
  ): void {
    if (field_name != 'items') return super.store__serialize_field(graph, props, container, field_name, field_value);

    let data__items = [];

    let items = this.items.map(item => item.find_source(graph));

    this.sort_items(items);

    //keep only items that have effect on generation runtime
    items = items.filter(item => item.has_effect_on_generation_runtime(graph));

    for (let item of items) {
      let data__item = item.store__serialize(graph, props);

      if (data__item.is_stub()) continue;
      if (data__item.is_empty()) debugger;

      data__items.push(data__item);
    }

    if (!data__items.is_empty()) container.set('items', data__items);
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  store__deserialize_field_value(field_name: string, value_from_this: any, field_value: any): any {
    if (field_name != 'items') return super.store__deserialize_field_value(field_name, value_from_this, field_value);

    if (!Array.isArray(field_value)) return;

    for (let [index, data_item] of field_value.entries()) {
      if (!UTILS__type.is_object(data_item)) continue;

      let item: T = this.create_item_new__default();

      let data_item_map: MAP__field_data = MAP__serializable.from_object(data_item);

      if (!item.store__can_deserialize_from_data(data_item_map)) continue;

      item.store__deserialize(data_item_map);

      let item_from_list = this.find_item_from_list(item);

      if (item_from_list.is_stub()) {
        this.items.push(item);
      } else {
        assert_true(item_from_list.store__can_deserialize_from_data(data_item_map));

        item_from_list.store__deserialize(data_item_map);
      }
    }

    this.field_name_and_path__fill_for_children();
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  find_item_from_list(item_to_compare: T): T {
    not_implemented();
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  populate_from<T_not_used extends FIELD__base>(graph: DTO_GRAPH__instance, other: T_not_used) {
    assert_array_is_empty(this.items);

    assert_instanceof(other, FIELD__list_base_generic<T>);

    let items = other.items.map(item => item.clone());

    for (let item of items) {
      item.meta.id_create();
      item.copy_meta_from_parent(item);

      let items_children = item.fields__get_map(true).values();

      for (let item_child of items_children) {
        item_child.id_create();
        item_child.copy_meta_from_parent(item);
      }
    }

    this.items = items;

    this.field_name_and_path__fill_for_children();
  }

  data_reset(graph: DTO_GRAPH__instance) {
    let fields_children = this.fields__get_map(true).values();

    let edges = graph.edges__get_related_to_fields(fields_children);

    graph.edges__delete(edges);

    this.items.length = 0;
  }

  /*
   *
   */

  assert_standard_fields(): void {
    super.assert_standard_fields();

    assert_type_enum(this.item_connectable_type, ENUM__field_connectable_type);

    assert_type_boolean(this.buttons_add_remove_are_disabled);
  }
}
